import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Compass, Sparkles, CheckCircle, Bell } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface GuideLog {
  id: string;
  created_at: string;
  guide_name: string;
  communication_type: string;
  message_content: string;
  guidance_area: string;
  sign_requested: string;
  sign_received: boolean;
  sign_received_date: string;
  sign_details: string;
  follow_up_notes: string;
}

interface CallingCard {
  id: string;
  guide_name: string;
  card_type: string;
  description: string;
  first_noticed_date: string;
  frequency: string;
  confidence_level: number;
  validation_notes: string;
  pattern_observations: string;
  created_at: string;
}

export default function SpiritGuideLog() {
  const [logs, setLogs] = useState<GuideLog[]>([]);
  const [callingCards, setCallingCards] = useState<CallingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    guide_name: '',
    communication_type: '',
    message_content: '',
    guidance_area: '',
    sign_requested: '',
    sign_received: false,
    sign_details: '',
    follow_up_notes: ''
  });

  const [cardFormData, setCardFormData] = useState({
    guide_name: '',
    card_type: '',
    description: '',
    first_noticed_date: '',
    frequency: 'occasional',
    confidence_level: 5,
    validation_notes: '',
    pattern_observations: ''
  });

  useEffect(() => {
    loadLogs();
    loadCallingCards();
  }, []);

  const loadLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('spirit_guide_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error loading logs', variant: 'destructive' });
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  };

  const loadCallingCards = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('spirit_guide_calling_cards')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error loading calling cards', variant: 'destructive' });
    } else {
      setCallingCards(data || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('spirit_guide_logs')
      .insert({
        user_id: user.id,
        sign_received_date: formData.sign_received ? new Date().toISOString().split('T')[0] : null,
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving log', variant: 'destructive' });
    } else {
      toast({ title: 'Spirit guide log saved' });
      setFormData({
        guide_name: '',
        communication_type: '',
        message_content: '',
        guidance_area: '',
        sign_requested: '',
        sign_received: false,
        sign_details: '',
        follow_up_notes: ''
      });
      setShowForm(false);
      loadLogs();
    }
  };

  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('spirit_guide_calling_cards')
      .insert({
        user_id: user.id,
        ...cardFormData
      });

    if (error) {
      toast({ title: 'Error saving calling card', variant: 'destructive' });
    } else {
      toast({ title: 'Calling card saved!' });
      setCardFormData({
        guide_name: '',
        card_type: '',
        description: '',
        first_noticed_date: '',
        frequency: 'occasional',
        confidence_level: 5,
        validation_notes: '',
        pattern_observations: ''
      });
      setShowCardForm(false);
      loadCallingCards();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading spirit guide log...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-primary animate-pulse" />
            Spirit Guide Connection
          </CardTitle>
          <CardDescription>
            Log communications with spirit guides and learn their unique "calling cards" - the signs that tell you spirit is near.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="logs" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="logs">
                <Compass className="h-4 w-4 mr-2" />
                Communications
              </TabsTrigger>
              <TabsTrigger value="cards">
                <Bell className="h-4 w-4 mr-2" />
                Calling Cards
              </TabsTrigger>
            </TabsList>

            <TabsContent value="logs" className="space-y-4">
              <Button onClick={() => setShowForm(!showForm)} className="bg-gradient-to-r from-secondary to-accent hover:opacity-90">
                <Plus className="h-4 w-4 mr-2" />
                Log Communication
              </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-2">
                <Label>Guide Name or Source</Label>
                <Input
                  value={formData.guide_name}
                  onChange={(e) => setFormData({ ...formData, guide_name: e.target.value })}
                  placeholder="Guide name, angel, higher self, etc."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Communication Type</Label>
                <Select value={formData.communication_type} onValueChange={(value) => setFormData({ ...formData, communication_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="How did you receive guidance?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meditation">During Meditation</SelectItem>
                    <SelectItem value="dream">Dream</SelectItem>
                    <SelectItem value="automatic-writing">Automatic Writing</SelectItem>
                    <SelectItem value="clairaudience">Heard (Clairaudience)</SelectItem>
                    <SelectItem value="clairvoyance">Seen (Clairvoyance)</SelectItem>
                    <SelectItem value="claircognizance">Just Knowing</SelectItem>
                    <SelectItem value="synchronicity">Synchronicity/Sign</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Guidance Area</Label>
                <Select value={formData.guidance_area} onValueChange={(value) => setFormData({ ...formData, guidance_area: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="What area of life?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="life-purpose">Life Purpose</SelectItem>
                    <SelectItem value="relationships">Relationships</SelectItem>
                    <SelectItem value="career">Career/Work</SelectItem>
                    <SelectItem value="spiritual-path">Spiritual Path</SelectItem>
                    <SelectItem value="health">Health</SelectItem>
                    <SelectItem value="decision">Decision Making</SelectItem>
                    <SelectItem value="personal-growth">Personal Growth</SelectItem>
                    <SelectItem value="general">General Guidance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Message or Guidance Received</Label>
                <Textarea
                  value={formData.message_content}
                  onChange={(e) => setFormData({ ...formData, message_content: e.target.value })}
                  placeholder="What guidance, wisdom, or message did you receive?"
                  rows={5}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Sign Requested (optional)</Label>
                <Input
                  value={formData.sign_requested}
                  onChange={(e) => setFormData({ ...formData, sign_requested: e.target.value })}
                  placeholder="Did you ask for a specific sign for validation?"
                />
              </div>

              {formData.sign_requested && (
                <>
                  <div className="flex items-center space-x-2 animate-fade-in">
                    <Checkbox
                      id="sign-received"
                      checked={formData.sign_received}
                      onCheckedChange={(checked) => setFormData({ ...formData, sign_received: checked as boolean })}
                    />
                    <label htmlFor="sign-received" className="text-sm font-light">
                      Sign was received
                    </label>
                  </div>

                  {formData.sign_received && (
                    <div className="space-y-2 animate-fade-in">
                      <Label>Sign Details</Label>
                      <Textarea
                        value={formData.sign_details}
                        onChange={(e) => setFormData({ ...formData, sign_details: e.target.value })}
                        placeholder="When and how did you receive the sign?"
                        rows={2}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label>Follow-up Notes</Label>
                <Textarea
                  value={formData.follow_up_notes}
                  onChange={(e) => setFormData({ ...formData, follow_up_notes: e.target.value })}
                  placeholder="Did you act on the guidance? What happened?"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-gradient-to-r from-secondary to-accent hover:opacity-90">Save Log</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start logging communications with your spirit guides to strengthen your connection.
              </p>
            ) : (
              logs.map((log) => (
                <Card key={log.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                          {log.guide_name}
                        </CardTitle>
                        <div className="flex gap-2 flex-wrap">
                          <Badge variant="outline">{log.communication_type}</Badge>
                          <Badge variant="secondary">{log.guidance_area}</Badge>
                          {log.sign_received && (
                            <Badge variant="default" className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Sign Received
                            </Badge>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.created_at), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-light text-primary mb-1">Guidance:</p>
                      <p className="text-sm text-muted-foreground font-light">{log.message_content}</p>
                    </div>
                    {log.sign_requested && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Sign Requested:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.sign_requested}</p>
                        {log.sign_details && (
                          <p className="text-sm text-muted-foreground font-light mt-1 italic">{log.sign_details}</p>
                        )}
                      </div>
                    )}
                    {log.follow_up_notes && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Follow-up:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.follow_up_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

              <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                <h4 className="font-light text-primary mb-2">🌟 Connecting with Spirit Guides</h4>
                <ul className="text-sm space-y-1 text-muted-foreground font-light">
                  <li>• Ask for guidance before sleep or meditation</li>
                  <li>• Request specific signs for validation</li>
                  <li>• Pay attention to repeating messages or themes</li>
                  <li>• Thank your guides after receiving guidance</li>
                  <li>• Act on guidance to strengthen the connection</li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="cards" className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-2">What are Calling Cards?</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Spirit guides often announce their presence through consistent signs - their "calling cards." These might be:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• A specific scent (perfume, flowers, cigarettes)</li>
                  <li>• A particular color you see when eyes are closed</li>
                  <li>• Tingles in the same spot on your body</li>
                  <li>• A light brush across your face or hair (like a web)</li>
                  <li>• Temperature changes</li>
                  <li>• Specific sounds or songs</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  Recording these signs helps you recognise patterns and know when spirit is present. Over time, you'll learn each guide's unique signature.
                </p>
              </div>

              <Button onClick={() => setShowCardForm(!showCardForm)} className="bg-gradient-to-r from-secondary to-accent hover:opacity-90">
                <Plus className="h-4 w-4 mr-2" />
                Log Calling Card
              </Button>

              {showCardForm && (
                <form onSubmit={handleCardSubmit} className="space-y-4 p-4 border border-border rounded-lg bg-card">
                  <div>
                    <Label>Guide Name</Label>
                    <Input
                      value={cardFormData.guide_name}
                      onChange={(e) => setCardFormData({ ...cardFormData, guide_name: e.target.value })}
                      placeholder="Which guide is this sign from?"
                      required
                    />
                  </div>

                  <div>
                    <Label>Type of Sign</Label>
                    <Select 
                      value={cardFormData.card_type} 
                      onValueChange={(value) => setCardFormData({ ...cardFormData, card_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sign type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scent">Scent/Smell</SelectItem>
                        <SelectItem value="color">Color (eyes closed)</SelectItem>
                        <SelectItem value="tingling">Tingling/Physical sensation</SelectItem>
                        <SelectItem value="touch">Light touch/brush</SelectItem>
                        <SelectItem value="temperature">Temperature change</SelectItem>
                        <SelectItem value="sound">Sound/Music</SelectItem>
                        <SelectItem value="visual">Visual sign</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={cardFormData.description}
                      onChange={(e) => setCardFormData({ ...cardFormData, description: e.target.value })}
                      placeholder="Describe the sign in detail..."
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <Label>When did you first notice this?</Label>
                    <Input
                      type="date"
                      value={cardFormData.first_noticed_date}
                      onChange={(e) => setCardFormData({ ...cardFormData, first_noticed_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label>Frequency</Label>
                    <Select 
                      value={cardFormData.frequency} 
                      onValueChange={(value) => setCardFormData({ ...cardFormData, frequency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="constant">Constant (every session)</SelectItem>
                        <SelectItem value="frequent">Frequent (most sessions)</SelectItem>
                        <SelectItem value="occasional">Occasional</SelectItem>
                        <SelectItem value="rare">Rare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Confidence Level: {cardFormData.confidence_level}/10</Label>
                    <Slider
                      value={[cardFormData.confidence_level]}
                      onValueChange={(value) => setCardFormData({ ...cardFormData, confidence_level: value[0] })}
                      max={10}
                      min={1}
                      step={1}
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      How certain are you this is a calling card?
                    </p>
                  </div>

                  <div>
                    <Label>Validation Notes (optional)</Label>
                    <Textarea
                      value={cardFormData.validation_notes}
                      onChange={(e) => setCardFormData({ ...cardFormData, validation_notes: e.target.value })}
                      placeholder="How has this sign been validated? Any confirmations?"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Pattern Observations (optional)</Label>
                    <Textarea
                      value={cardFormData.pattern_observations}
                      onChange={(e) => setCardFormData({ ...cardFormData, pattern_observations: e.target.value })}
                      placeholder="What patterns have you noticed? When does it appear most?"
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" className="bg-gradient-to-r from-secondary to-accent hover:opacity-90">Save Calling Card</Button>
                    <Button type="button" variant="outline" onClick={() => setShowCardForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              <div className="space-y-3">
                {callingCards.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No calling cards recorded yet. Start noticing patterns - what unique signs does each guide use to announce their presence?
                  </p>
                ) : (
                  callingCards.map((card) => (
                    <Card key={card.id} className="bg-muted/30">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Bell className="h-4 w-4 text-primary" />
                              {card.guide_name}
                            </CardTitle>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              <Badge variant="outline">{card.card_type}</Badge>
                              <Badge variant="secondary">{card.frequency}</Badge>
                              <Badge>{card.confidence_level}/10 confidence</Badge>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold mb-1">The Sign:</p>
                          <p className="text-sm text-muted-foreground">{card.description}</p>
                        </div>
                        {card.first_noticed_date && (
                          <p className="text-xs text-muted-foreground">
                            First noticed: {new Date(card.first_noticed_date).toLocaleDateString()}
                          </p>
                        )}
                        {card.validation_notes && (
                          <div>
                            <p className="text-sm font-semibold mb-1">Validation:</p>
                            <p className="text-sm text-muted-foreground">{card.validation_notes}</p>
                          </div>
                        )}
                        {card.pattern_observations && (
                          <div>
                            <p className="text-sm font-semibold mb-1">Patterns:</p>
                            <p className="text-sm text-muted-foreground">{card.pattern_observations}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
